import { useState } from "react";
import { FileTextIcon, ImageIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ArchivoDeAntecedente } from "@/lib/informes";

import { esImagen, etiquetaDeLaSeccionDelCliente, fechaDeSubida } from "./documentosDeLaPestana";
import { SelectorDeClasificacion, type Clasificacion } from "./SelectorDeClasificacion";
import { formatearPeso } from "./ZonaDeImagen";

/**
 * Lo que el cliente mandó por el portal y todavía nadie ordenó.
 *
 * Va ARRIBA de la lista de documentos y desaparece cuando no queda nada: es
 * trabajo pendiente, y mientras exista la IA no lee esos archivos. Por eso el
 * aviso va en el encabezado y no escondido en un tooltip.
 *
 * Cada archivo conserva el espacio del portal por el que llegó (la etiqueta),
 * que es la única pista que tiene quien clasifica de lo que el cliente creyó
 * estar mandando.
 */
export function ArchivosSinClasificar({
  archivos,
  urlDeDescarga,
  clasificacion,
}: {
  archivos: ArchivoDeAntecedente[];
  urlDeDescarga: (archivoId: number) => string;
  clasificacion: Clasificacion;
}) {
  const [abierto, setAbierto] = useState<number | null>(null);

  if (archivos.length === 0) return null;

  return (
    <section
      aria-label="Enviados por el cliente sin clasificar"
      className="bg-card overflow-hidden rounded-xl border-[1.5px]"
    >
      <header className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 px-5 pt-3.5 pb-2.5">
        <span
          aria-hidden="true"
          className="border-warning-dot bg-warning-dot size-[0.5625rem] flex-none self-center rounded-full border-[1.5px]"
        />
        <h3 className="text-ink text-[0.84375rem] font-bold">Enviados por el cliente sin clasificar</h3>
        <span className="text-ink font-mono text-[0.78125rem] font-bold">{archivos.length}</span>
        <p className="text-ink-muted w-full text-[0.71875rem] font-semibold">
          La IA no los lee hasta que los clasifiques.
        </p>
      </header>

      <ul className="flex flex-col gap-1.5 px-5 pb-4">
        {archivos.map((archivo) => {
          const Icono = esImagen(archivo.nombre_original) ? ImageIcon : FileTextIcon;

          return (
            <li key={archivo.id} className="bg-muted/40 rounded-[0.625rem] border px-2.5 py-1">
              <div className="flex min-h-8 flex-wrap items-center gap-x-2.5 gap-y-1">
                <Icono className="text-ink-hint size-3.5 flex-none" aria-hidden="true" />

                <span
                  className="text-ink min-w-0 flex-1 truncate text-[0.78125rem] font-semibold"
                  title={archivo.nombre_original}
                >
                  {archivo.nombre_original}
                </span>

                <span className="text-ink-muted flex-none font-mono text-[0.65625rem] font-semibold">
                  {formatearPeso(archivo.archivo_bytes)}
                </span>

                {archivo.seccion_cliente ? (
                  <Badge variant="info">{etiquetaDeLaSeccionDelCliente(archivo.seccion_cliente)}</Badge>
                ) : null}

                <span className="text-ink-muted min-w-[4.625rem] flex-none text-right font-mono text-[0.65625rem] font-semibold">
                  {fechaDeSubida(archivo.subido_en)}
                </span>

                <a
                  href={urlDeDescarga(archivo.id)}
                  aria-label={`Ver ${archivo.nombre_original}`}
                  className="text-ink-body hover:bg-muted rounded-md px-2 py-1 text-[0.71875rem] font-bold"
                >
                  Ver
                </a>

                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={`Clasificar ${archivo.nombre_original}`}
                  aria-expanded={abierto === archivo.id}
                  onClick={() => setAbierto(abierto === archivo.id ? null : archivo.id)}
                >
                  Clasificar
                </Button>
              </div>

              {abierto === archivo.id ? (
                <SelectorDeClasificacion
                  nombreDelArchivo={archivo.nombre_original}
                  clasificacion={clasificacion}
                  onClasificar={(tipoId) => clasificacion.onClasificar(archivo.id, tipoId)}
                  onCerrar={() => setAbierto(null)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
